import {
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Container,
  Divider,
  Link as MuiLink,
  LinkProps,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import logo from './images/logo-white.png'
import { styled } from '@mui/material/styles'
import ApplicationLinks from '../../ApplicationLinks'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import {
  COMPANY_LEGAL_NAME,
  OFFICE_ADDRESS_LINES,
  SUPPORT_EMAIL,
  SUPPORT_PHONE_DISPLAY_PUBLIC,
  SUPPORT_PHONE_E164,
} from '../../utils/supportContact'

type FooterLinkProps = LinkProps & { to?: string }

const FooterLink = styled(MuiLink)<FooterLinkProps>(
  ({ theme }) => `
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color 0.3s ease;
    &:hover {
      color: ${theme.palette.common.white};
      text-decoration: underline;
    }
  `
)

const FooterSectionTitle = styled(Typography)(
  () => `
    color: white;
    font-weight: 600;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  `
)

export const WebsiteFooter = () => {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const theme = useTheme()
  useMediaQuery(theme.breakpoints.down('md'))
  useMediaQuery(theme.breakpoints.down('sm'))

  const handleWhatsAppClick = () => {
    window.open(`https://web.whatsapp.com/send?phone=${SUPPORT_PHONE_E164}`, '_blank')
  }

  const handlePhoneClick = () => {
    window.open(`tel:+${SUPPORT_PHONE_E164}`, '_self')
  }

  const handleEmailClick = () => {
    window.open(`mailto:${SUPPORT_EMAIL}`, '_self')
  }

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#1e3a8a',
        color: 'white',
        paddingTop: { xs: 4, md: 6 },
        paddingBottom: { xs: 3, md: 4 },
        marginTop: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info Section */}
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ mb: { xs: 3, md: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img 
                  src={logo} 
                  alt="Scaffolding & Shuttering App" 
                  style={{ width: 50, height: 36, marginRight: 12 }} 
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1.3rem',
                  }}
                >
                  Scaffoldingapp.Com
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  lineHeight: 1.8,
                  mb: 2,
                }}
              >
                Comprehensive billing and inventory management solution for 
                shuttering and scaffolding businesses across India.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.875rem',
                }}
              >
                © {currentYear} TEJSE
                <br />
                {COMPANY_LEGAL_NAME}
                <br />
                All rights reserved.
              </Typography>
            </Box>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={2}>
            <FooterSectionTitle variant="h6">Quick Links</FooterSectionTitle>
            <Box
              component="nav"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.productsLink()}
                sx={{ fontSize: '0.95rem' }}
              >
                Products
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.aboutUsLink()}
                sx={{ fontSize: '0.95rem' }}
              >
                About Us
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.pricingLink()}
                sx={{ fontSize: '0.95rem' }}
              >
                Pricing
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.contactLink()}
                sx={{ fontSize: '0.95rem' }}
              >
                Contact Us
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to="/tutorials"
                sx={{ fontSize: '0.95rem' }}
              >
                Tutorials
              </FooterLink>
            </Box>
          </Grid>

          {/* Legal Section */}
          <Grid item xs={12} sm={6} md={2}>
            <FooterSectionTitle variant="h6">Legal</FooterSectionTitle>
            <Box
              component="nav"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.privacyPolicyLink()}
                sx={{ fontSize: '0.95rem' }}
              >
                Privacy Policy
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.termsAndConditionsink()}
                sx={{ fontSize: '0.95rem' }}
              >
                Terms & Conditions
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.shippingPolicyLink()}
                sx={{ fontSize: '0.95rem' }}
              >
                Shipping Policy
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to={ApplicationLinks.refundAndCancelationLink()}
                sx={{ fontSize: '0.95rem' }}
              >
                Refund Policy
              </FooterLink>
              <FooterLink
                component={RouterLink}
                to="/account-data-deletion"
                sx={{ fontSize: '0.95rem' }}
              >
                Account & Data deletion
              </FooterLink>
            </Box>
          </Grid>

          {/* Contact Section */}
          <Grid item xs={12} sm={6} md={4}>
            <FooterSectionTitle variant="h6">Get in Touch</FooterSectionTitle>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  '&:hover': {
                    '& .contact-text': {
                      color: 'white',
                    },
                  },
                }}
                onClick={handlePhoneClick}
              >
                <PhoneIcon
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: 20,
                  }}
                />
                <FooterLink
                  component="span"
                  className="contact-text"
                  sx={{ fontSize: '0.95rem' }}
                >
                  {SUPPORT_PHONE_DISPLAY_PUBLIC}
                </FooterLink>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  '&:hover': {
                    '& .contact-text': {
                      color: 'white',
                    },
                  },
                }}
                onClick={handleWhatsAppClick}
              >
                <WhatsAppIcon
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: 20,
                  }}
                />
                <FooterLink
                  component="span"
                  className="contact-text"
                  sx={{ fontSize: '0.95rem' }}
                >
                  WhatsApp: {SUPPORT_PHONE_DISPLAY_PUBLIC}
                </FooterLink>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  '&:hover': {
                    '& .contact-text': {
                      color: 'white',
                    },
                  },
                }}
                onClick={handleEmailClick}
              >
                <EmailIcon
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: 20,
                  }}
                />
                <FooterLink
                  component="span"
                  className="contact-text"
                  sx={{ fontSize: '0.95rem' }}
                >
                  {SUPPORT_EMAIL}
                </FooterLink>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: 20,
                    mt: 0.25,
                  }}
                />
                <Typography
                  component="span"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                  }}
                >
                  {OFFICE_ADDRESS_LINES.map((line, index) => (
                    <span key={line}>
                      {line}
                      {index < OFFICE_ADDRESS_LINES.length - 1 && <br />}
                    </span>
                  ))}
                </Typography>
              </Box>
            </Box>
            
          </Grid>
        </Grid>

        {/* Bottom Divider and Copyright */}
        <Divider
          sx={{
            borderColor: 'rgba(255, 255, 255, 0.2)',
            my: 4,
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.875rem',
            }}
          >
            © {currentYear} {COMPANY_LEGAL_NAME}. All rights reserved.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.875rem',
            }}
          >
            Made with ❤️ for scaffolding businesses across India
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}
